import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatDateToDDMMYY,
  formatTimeRange,
  Checkbox,
  BaseButton,
} from '@repo/common';
import OptionList from './OptionList';
import { WORKSHOP_COLUMN_CLASS as COL } from './workshopColumns';
import { getWorkshopCategoryLabel } from '../../Kpathport/utils/workshopCategory';
import { ReactComponent as ImageIcon } from '@repo/common/assets/icons/24px/icon_24px_image_filled.svg';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';

const WorkshopRow = ({
  item,
  checked,
  onSelect,
  isDeleted,
  onDeletePermanentlyClick,
  onDeleteClick,
  onActionSuccess,
}) => {
  const navigate = useNavigate();
  const [showOptionList, setShowOptionList] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const isFull = item.currentParticipants >= item.maxParticipants;
  const isExistingExtraSeats = item.currentParticipants > item.maxParticipants;
  const isHidden = item.isHidden;

  const formatCategory = (category) =>
    category ? getWorkshopCategoryLabel(category) : '-';

  const formatStatus = (status) => {
    if (!status) return '-';
    if (status === 'OPEN' && isFull) {
      return 'Full';
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const goToDetail = () =>
    navigate(`/admin/workshop/${item.workshopId}?slotId=${item.slotId}`);

  const openOptions = (e) => {
    e.stopPropagation();
    setAnchorRect(e.currentTarget.getBoundingClientRect());
    setShowOptionList(true);
  };

  const dateText = item?.date ? formatDateToDDMMYY(item.date) : '-';
  const timeText = formatTimeRange(item?.startTime, item?.endTime);
  const statusText = formatStatus(item.status);
  const confirmedText = isExistingExtraSeats
    ? `${item.currentParticipants - 1}+1`
    : item.currentParticipants;
  const hasWaitlist = isFull || item?.waitlistedCount || item?.offeredCount;

  const checkbox = (
    <div
      className={`flex items-center ${COL.check}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Checkbox checked={checked} onChange={(e) => onSelect(e.target.checked)} />
    </div>
  );

  const thumbnail = item?.thumbnailUrl ? (
    <img
      src={item.thumbnailUrl}
      alt='workshop'
      className='w-6 h-6 object-cover object-center flex-shrink-0'
    />
  ) : (
    <ImageIcon className='w-5 h-5 text-text-tertiary flex-shrink-0' />
  );

  const deletedTag = (
    <BaseButton
      type='text'
      size='s'
      isStatic
      hideIcon
      bgclass='bg-grayscale-gray03 px-1 py-0.5 h-[18px] rounded-[4px]'
      textclass='text-grayscale-gray00 typo-b11'
      width='w-[48px]'
    >
      Deleted
    </BaseButton>
  );

  const optionButton = (
    <button
      type='button'
      aria-label='Workshop options'
      className='text-icon-placeholder hover:text-icon-tertiary active:text-icon-tertiary'
      onClick={openOptions}
    >
      <OptionIcon />
    </button>
  );

  return (
    <>
      {/* 표 행 — tabletV(1180px) 이상 */}
      <div
        className='hidden tabletV:flex w-full h-[48px] items-center bg-grayscale-gray00 hover:bg-grayscale-gray01 rounded-lg cursor-pointer'
        onClick={goToDetail}
      >
        {checkbox}
        <div className={`flex items-center ${COL.thumb}`}>{thumbnail}</div>

        {isDeleted && (
          <div className={`flex justify-center items-center px-2 ${COL.deletedAt}`}>
            <span className='typo-b14 text-text-secondary block w-full text-center truncate'>
              {item?.deletedAt ? formatDateToDDMMYY(item.deletedAt) : '-'}
            </span>
          </div>
        )}

        {isDeleted && (
          <div className={`flex justify-center items-center px-2 gap-2 ${COL.name}`}>
            {deletedTag}
            <span className='typo-b14Strong text-text-primary truncate'>{item?.title}</span>
          </div>
        )}

        <div className={`flex justify-center items-center px-2 ${COL.date}`}>
          <span className='typo-b14 text-text-secondary block w-full text-center truncate'>
            {dateText}
          </span>
        </div>

        <div className={`flex justify-center items-center px-2 ${isDeleted ? COL.timeDeleted : COL.time}`}>
          <span className='typo-b14 text-text-secondary'>{timeText}</span>
        </div>

        {!isDeleted && (
          <div className={`flex justify-center items-center px-2 ${COL.category}`}>
            <span className='typo-b14 text-text-secondary'>{formatCategory(item?.category)}</span>
          </div>
        )}

        {!isDeleted && (
          <div className={`flex justify-center items-center px-2 ${COL.name}`}>
            <span className='typo-b14Strong text-text-primary truncate'>{item?.title}</span>
          </div>
        )}

        <div className={`flex justify-start items-center px-2 gap-1 typo-b14 ${COL.confirmed}`}>
          <span className='text-text-accent'>{confirmedText}</span>
          <span className='text-text-secondary'>
            ({item.currentParticipants}/{item.maxParticipants})
          </span>
        </div>

        {isDeleted ? (
          <div className={`flex justify-start items-center px-2 gap-2 typo-b14 text-text-secondary ${COL.impact}`}>
            <span>W: {item?.waitlistedCount}</span>
            <span>B: {item?.currentParticipants}</span>
          </div>
        ) : (
          <div className={`flex justify-start items-center px-2 gap-2 typo-b14 text-text-secondary ${COL.waitlist}`}>
            {hasWaitlist ? (
              <>
                <span>W: {item?.waitlistedCount}</span>
                <span>O: {item?.offeredCount}</span>
              </>
            ) : (
              '-'
            )}
          </div>
        )}

        <div className={`flex flex-col justify-center items-center px-2 ${COL.status}`}>
          <span className='typo-b14 text-text-secondary'>{statusText}</span>
          {isHidden && <span className='typo-b14 text-text-secondary'>(Hidden)</span>}
        </div>

        <div className={`flex justify-center items-center ${COL.options}`}>{optionButton}</div>
      </div>

      {/* 카드 — tabletV 미만 */}
      <div
        className='tabletV:hidden flex flex-col gap-2 w-full px-3 py-3 rounded-2xl border border-grayscale-gray02 bg-grayscale-gray00 active:bg-grayscale-gray01 cursor-pointer'
        onClick={goToDetail}
      >
        <div className='flex items-start gap-2'>
          <div className='-ml-2'>{checkbox}</div>
          <div className='flex flex-col min-w-0 flex-1 gap-1'>
            <div className='flex items-center gap-2 min-w-0'>
              {thumbnail}
              {isDeleted && deletedTag}
              <span className='typo-b14Strong text-text-primary truncate'>{item?.title}</span>
            </div>
            <div className='flex flex-wrap gap-x-2 typo-b13 text-text-secondary'>
              <span>{dateText}</span>
              <span>{timeText}</span>
              {!isDeleted && <span>{formatCategory(item?.category)}</span>}
              {isDeleted && item?.deletedAt && (
                <span className='text-text-placeholder'>
                  Deleted {formatDateToDDMMYY(item.deletedAt)}
                </span>
              )}
            </div>
          </div>
          <div className='flex-shrink-0 -mr-1' onClick={(e) => e.stopPropagation()}>
            {optionButton}
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1 typo-b13'>
          <span className='text-text-secondary'>
            Confirmed <span className='text-text-accent'>{confirmedText}</span> ({item.currentParticipants}/
            {item.maxParticipants})
          </span>
          {isDeleted ? (
            <span className='text-text-secondary'>
              W: {item?.waitlistedCount} · B: {item?.currentParticipants}
            </span>
          ) : (
            hasWaitlist && (
              <span className='text-text-secondary'>
                W: {item?.waitlistedCount} · O: {item?.offeredCount}
              </span>
            )
          )}
          <span className='text-text-primary typo-b13Strong'>
            {statusText}
            {isHidden && ' (Hidden)'}
          </span>
        </div>
      </div>

      {showOptionList && (
        <OptionList
          anchorRect={anchorRect}
          onClose={() => setShowOptionList(false)}
          isDeleted={isDeleted}
          isHidden={isHidden}
          slotId={item.slotId}
          onActionSuccess={onActionSuccess}
          onDeletePermanentlyClick={() => {
            setShowOptionList(false);
            onDeletePermanentlyClick?.(item.slotId);
          }}
          onDeleteClick={() => {
            setShowOptionList(false);
            onDeleteClick?.(item.slotId);
          }}
          status={item.status}
        />
      )}
    </>
  );
};

export default WorkshopRow;
